import type { Dispatch, UnknownAction } from '@reduxjs/toolkit'

import { feedbackCleared, FEEDBACK_SCOPES } from '../../../shared/model/feedbackSlice'
import { ridKey } from '../../../shared/model/keys'
import {
  refundCancelRequested,
  refundCreateRequested,
  refundFiscalizeRequested,
  refundPayRequested,
  refundReconcileRequested,
  refundReloadRequested,
  refundStatusCheckRequested,
} from './commands'
import { refundQuantityNumber } from './quantity'
import {
  refundDraftCleared,
  refundFinishedDismissed,
  refundQuantitiesFilled,
  refundQuantityChanged,
} from './refundsSlice'
import type { RefundActions, RefundVm } from './types'

/** Переходы окна возврата — их даёт binding (окна — забота оболочки, не пакета). */
export interface RefundNavigation {
  openRmk: () => void
  openSale: () => void
  openRefund: (receiptId: string) => void
  openResolve: (operationId: string) => void
  close: () => void
}

/**
 * Действия окна возврата → команды. Место и версии берутся из vm — из того,
 * что человек ВИДИТ: служба отвергнет команду по устаревшей версии (409), и
 * человек увидит возврат с сервера, а не тихую перезапись.
 */
export function createRefundActions(
  vm: RefundVm,
  seat: { registerKey: string; deviceId: number; sessionVersion: number } | null,
  dispatch: Dispatch<UnknownAction>,
  navigation: RefundNavigation,
): RefundActions {
  const ref = { resourceId: vm.resourceId, receiptId: vm.receiptId }
  const seatRef = seat === null ? null : { resourceId: vm.resourceId, registerKey: seat.registerKey, deviceId: seat.deviceId }
  const refundCommand = () =>
    seatRef === null || vm.refund === null
      ? null
      : { ...seatRef, receiptId: vm.receiptId, refundId: vm.refund.id, expectedVersion: vm.refund.version }
  return {
    quantityChanged: (ordinal, quantity) => dispatch(refundQuantityChanged({ ...ref, ordinal, quantity })),
    returnAll: () => {
      const quantities: Record<string, string> = {}
      for (const line of vm.lines) {
        if (!line.exhausted) quantities[String(line.ordinal)] = line.availableValue
      }
      dispatch(refundQuantitiesFilled({ ...ref, quantities }))
    },
    clear: () => dispatch(refundDraftCleared(ref)),
    startNew: () => dispatch(refundFinishedDismissed(ref)),
    create: () => {
      if (!vm.canCreate || seatRef === null || seat === null) return
      const lines = vm.lines
        .filter((line) => line.quantity !== '')
        .map((line) => ({ ordinal: line.ordinal, quantity: refundQuantityNumber(line.quantity) }))
      dispatch(refundCreateRequested({ ...seatRef, receiptId: vm.receiptId, lines, expectedVersion: seat.sessionVersion }))
    },
    pay: () => {
      const command = refundCommand()
      if (command !== null && vm.canPay) dispatch(refundPayRequested(command))
    },
    fiscalize: () => {
      const command = refundCommand()
      if (command !== null && vm.canFiscalize) dispatch(refundFiscalizeRequested(command))
    },
    cancel: () => {
      const command = refundCommand()
      if (command !== null && vm.canCancel) dispatch(refundCancelRequested(command))
    },
    checkStatus: () => {
      if (seatRef === null) {
        dispatch(refundReloadRequested(ref))
        return
      }
      dispatch(
        refundStatusCheckRequested({ ...seatRef, receiptId: vm.receiptId, operationId: vm.operation?.operationId ?? null }),
      )
    },
    reconcile: () => {
      const operation = vm.operation
      if (seatRef === null || operation === null || operation.version === null || !operation.canReconcile) return
      dispatch(
        refundReconcileRequested({
          ...seatRef,
          receiptId: vm.receiptId,
          operationId: operation.operationId,
          expectedVersion: operation.version,
        }),
      )
    },
    openManualResolve: () => {
      if (vm.operation?.manualRequired === true) navigation.openResolve(vm.operation.operationId)
    },
    openBlocked: () => {
      const action = vm.blockedAction
      if (action === null) return
      if (action.kind === 'rmk') navigation.openRmk()
      else if (action.kind === 'sale') navigation.openSale()
      else navigation.openRefund(action.receiptId)
    },
    reload: () => dispatch(refundReloadRequested(ref)),
    close: navigation.close,
    dismissFeedback: () => dispatch(feedbackCleared({ key: ridKey(vm.resourceId, FEEDBACK_SCOPES.refund(vm.receiptId)) })),
  }
}
